/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useEffect } from "react";
import { Table as MuiTable } from "@mui/material";
import { TableBody } from "@mui/material";
import { TableCell } from "@mui/material";
import { TableContainer } from "@mui/material";
import { TableHead } from "@mui/material";
import { TableRow } from "@mui/material";
import { Paper } from "@mui/material";
import { Box } from "@mui/material";
import { Typography } from "@mui/material";
import { Pagination } from "@mui/material";
import { PaginationItem } from "@mui/material";
import { ReactComponent as PreviousIcon } from "cms/assets/icons/previous.svg";
import { ReactComponent as NextIcon } from "cms/assets/icons/next.svg";
import { Dropdown } from "cms/shared/dropdown";
import { columns } from "./columns.js";
import { formatDateTime } from "./date-time";

const Table = (props) => {

    const data = props?.data;
    const onPageChange = props?.onPageChange;
    const onPageSizeChange = props?.onPageSizeChange;

    // Use props for pagination instead of local state
    const currentPageNumber = props?.currentPage || 1;
    const itemsPerPage = props?.pageSize || 10;
    const totalItemCount = props?.pagination?.total || 0;

    const [tableData, setTableData] = useState([]);

    // Helper functions for CSS class names
    const getRowClassName = (rowIndex) => {
        return rowIndex % 2 === 0 ? "table-row-even" : "table-row-odd";
    };

    const getHeaderCellClassName = (columnIndex, totalColumns) => {
        const baseClass = "table-header-cell";
        const borderClass = columnIndex < totalColumns - 1 ? "has-border-right" : "";
        return `${baseClass} ${borderClass}`.trim();
    };

    const getBodyCellClassName = (columnIndex, totalColumns) => {
        const baseClass = "table-body-cell";
        const borderClass = columnIndex < totalColumns - 1 ? "has-border-right" : "";
        return `${baseClass} ${borderClass}`.trim();
    };

    // Use data directly from props (server-side pagination)
    useEffect(() => {
        setTableData(data || []);
    }, [data]);

    const handlePageChange = (event, newPageNumber) => {
        onPageChange?.(event, newPageNumber);
    };

    const handleItemsPerPageChange = (event) => {
        onPageSizeChange?.(event);
    };

    const totalPageCount = Math.ceil(totalItemCount / itemsPerPage);

    // Function to render evidence
    const renderEvidenceLinks = (evidenceArray) => {
        if (!evidenceArray || evidenceArray?.length === 0) {
            return "";
        }

        return (
            <Box className="evidence-container">
                {evidenceArray?.map((evidenceItem, itemIndex) => {
                    return (
                        <Box
                            key={`${evidenceItem?.filename}-${itemIndex}`}
                            component="a"
                            href={evidenceItem?.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="evidence-link"
                        >
                            {evidenceItem?.title || evidenceItem?.filename}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // Generate page numbers for pagination (max 6 pages shown)
    // const calculateVisiblePageRange = () => {
    //     const maximumVisiblePages = 6;
    //     let visibleStartPage = Math.max(1, currentPageNumber - Math.floor(maximumVisiblePages / 2));
    //     let visibleEndPage = Math.min(totalPageCount, visibleStartPage + maximumVisiblePages - 1);
    //
    //     if (visibleEndPage - visibleStartPage + 1 < maximumVisiblePages) {
    //         visibleStartPage = Math.max(1, visibleEndPage - maximumVisiblePages + 1);
    //     }
    //
    //     return { startPage: visibleStartPage, endPage: visibleEndPage };
    // };

    return (
        <div className='simplicity-table'>
            <Paper className="table-paper-container">
                <TableContainer className="table-container">
                    <MuiTable
                        stickyHeader
                        className="table-base"
                    >
                        <TableHead>
                            <TableRow>
                                {columns?.map((columnDefinition, columnIndex) => (
                                    <TableCell
                                        key={`${columnDefinition?.id}_${columnIndex}`}
                                        className={getHeaderCellClassName(columnIndex, columns?.length || 0)}
                                        style={{
                                            minWidth: columnDefinition?.minWidth,
                                            maxWidth: columnDefinition?.maxWidth
                                        }}
                                    >
                                        {columnDefinition?.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(tableData?.length || 0) === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns?.length || 0} align="center">
                                        <Typography>No data found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tableData?.map((dataRow, rowIndex) => (
                                    <TableRow
                                        key={`${dataRow?.id}_${rowIndex}`}
                                        className={getRowClassName(rowIndex)}
                                    >
                                        {columns?.map((columnDefinition, columnIndex) => (
                                            <TableCell
                                                key={columnDefinition?.id}
                                                className={getBodyCellClassName(columnIndex, columns?.length || 0)}
                                                style={{
                                                    minWidth: columnDefinition?.minWidth,
                                                    maxWidth: columnDefinition?.maxWidth
                                                }}
                                            >
                                                {columnDefinition?.id === "evidence" ?
                                                    renderEvidenceLinks(dataRow?.[columnDefinition?.id]) :
                                                    (columnDefinition?.id === "date" || columnDefinition?.id === "time") ?
                                                        formatDateTime(dataRow, columnDefinition?.id) :
                                                        dataRow?.[columnDefinition?.id]
                                                }
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </MuiTable>
                </TableContainer>

                {/* Pagination Controls */}
                <Box className="pagination-container">

                    <Box className="pagination-wrapper">
                        <Pagination
                            count={totalPageCount}
                            page={currentPageNumber}
                            onChange={handlePageChange}
                            color="primary"
                            siblingCount={2}
                            boundaryCount={1}
                            className="pagination-base"
                            renderItem={(item) => (
                                <PaginationItem
                                    slots={{
                                        previous: () => (
                                            <Box className="pagination-button-base">
                                                <PreviousIcon />
                                            </Box>
                                        ),
                                        next: () => (
                                            <Box className="pagination-button-base">
                                                <NextIcon />
                                            </Box>
                                        )
                                    }}
                                    {...item}
                                    className={`pagination-item ${item.selected ? "pagination-selected" : ""}`}
                                />
                            )}
                        />
                        <Box className="items-per-page-container">
                            <Typography component="div" className="items-per-page-label">
                                Items per page:
                            </Typography>
                            <Dropdown
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                options={[
                                    { value: 5, label: "5" },
                                    { value: 10, label: "10" },
                                    { value: 25, label: "25" },
                                    { value: 50, label: "50" }
                                ]}
                                fullWidth={false}
                                width="88px"
                            />
                        </Box>


                    </Box>
                </Box>
            </Paper>
        </div>
    );

};

Table.displayName = "Table";
export { Table };
